import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, formatCurrency, formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../i18n';
import { 
  Wallet, Banknote, TrendingUp, Calendar,
  Loader2, Filter, Download, ArrowUpRight
} from 'lucide-react';

export const ContributionsPage = () => {
  const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100', 'all'];
  const ALL_FETCH_CHUNK = 200;
  const { t } = useLanguage();
  const [contributions, setContributions] = useState([]);
  const [tontines, setTontines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [filterTontine, setFilterTontine] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('20');
  const [totalContributions, setTotalContributions] = useState(0);
  const [totalContributedAmount, setTotalContributedAmount] = useState(0);

  useEffect(() => {
    loadTontines();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterTontine]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    loadContributionsPage();
  }, [filterTontine, currentPage, pageSize]);

  const loadTontines = async () => {
    try {
      const tontinesRes = await api.getTontines();
      setTontines(tontinesRes.data);
    } catch (error) {
      console.error('Error loading tontines:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContributionsPage = async () => {
    setPageLoading(true);
    try {
      const isAllMode = pageSize === 'all';
      const baseParams = filterTontine !== 'all' ? { tontine_id: filterTontine } : {};

      if (isAllMode) {
        let offset = 0;
        let allItems = [];
        let totalCount = 0;
        let totalAmount = 0;

        do {
          const contribRes = await api.getContributions({
            ...baseParams,
            limit: ALL_FETCH_CHUNK,
            offset,
          });

          const items = contribRes.data || [];
          const totalCountRaw = contribRes.headers?.['x-total-count'];
          const totalAmountRaw = contribRes.headers?.['x-total-amount'];

          if (totalCount === 0 && Number.isFinite(Number(totalCountRaw))) {
            totalCount = Number(totalCountRaw);
          }
          if (totalAmount === 0 && Number.isFinite(Number(totalAmountRaw))) {
            totalAmount = Number(totalAmountRaw);
          }

          allItems = [...allItems, ...items];
          offset += ALL_FETCH_CHUNK;

          if (items.length === 0) {
            break;
          }
        } while (totalCount === 0 || allItems.length < totalCount);

        setContributions(allItems);
        setTotalContributions(totalCount || allItems.length);
        setTotalContributedAmount(
          totalAmount || allItems.reduce((sum, contribution) => sum + (Number(contribution.amount) || 0), 0)
        );
      } else {
        const numericPageSize = Number(pageSize);
        const offset = (currentPage - 1) * numericPageSize;
        const params = {
          ...baseParams,
          limit: numericPageSize,
          offset,
        };

        const contribRes = await api.getContributions(params);
        const items = contribRes.data || [];
        setContributions(items);

        const totalCountRaw = contribRes.headers?.['x-total-count'];
        const totalAmountRaw = contribRes.headers?.['x-total-amount'];

        setTotalContributions(Number.isFinite(Number(totalCountRaw)) ? Number(totalCountRaw) : items.length);
        setTotalContributedAmount(
          Number.isFinite(Number(totalAmountRaw))
            ? Number(totalAmountRaw)
            : items.reduce((sum, contribution) => sum + (Number(contribution.amount) || 0), 0)
        );
      }
    } catch (error) {
      console.error('Error loading contributions page:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const pageSizeValue = pageSize === 'all' ? Math.max(totalContributions, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalContributions / pageSizeValue));
  const safePage = Math.min(currentPage, totalPages);

  const totalContributed = totalContributedAmount;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contributions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
            Mes Cotisations
          </h1>
          <p className="text-stone-600 dark:text-stone-400 mt-1">
            Historique de vos versements
          </p>
        </div>
        <Button variant="outline" className="gap-2" data-testid="export-contributions-btn">
          <Download className="w-4 h-4" />
          Exporter
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Total versé</p>
                <p className="text-xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {formatCurrency(totalContributed)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Banknote className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Transactions</p>
                <p className="text-xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {totalContributions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card col-span-2 lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Tontines actives</p>
                <p className="text-xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {tontines.filter(t => t.status === 'active').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <Filter className="w-4 h-4" />
          Filtrer par tontine
        </div>
        <Select value={filterTontine} onValueChange={setFilterTontine}>
          <SelectTrigger className="w-full sm:w-[240px]" data-testid="filter-tontine-select">
            <SelectValue placeholder="Toutes les tontines" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les tontines</SelectItem>
            {tontines.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contributions List */}
      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="font-display text-lg">
            Historique ({totalContributions})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {totalContributions === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 mb-4">Aucune cotisation enregistrée</p>
              <Link to="/tontines">
                <Button variant="outline">Voir mes tontines</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {contributions.map((contrib) => {
                const tontine = tontines.find(t => t.id === contrib.tontine_id);
                return (
                  <div 
                    key={contrib.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                    data-testid={`contribution-item-${contrib.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                        <ArrowUpRight className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 dark:text-stone-50">
                          {tontine?.name || 'Tontine'}
                        </p>
                        <p className="text-sm text-stone-500">
                          {formatDate(contrib.created_at)} • Tour {contrib.round_number}
                        </p>
                      </div>
                    </div>
                    <div className="w-full sm:w-auto text-left sm:text-right">
                      <p className="font-bold text-green-600 tabular-nums text-lg">
                        +{formatCurrency(contrib.amount, tontine?.currency || 'XOF')}
                      </p>
                      <Badge variant="outline" className="text-xs capitalize">
                        {contrib.payment_method.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                );
              })}

              <div className="mt-4 flex items-center justify-between gap-2" data-testid="my-contributions-pagination">
                <p className="text-xs text-stone-500" data-testid="my-contributions-page-indicator">
                  {t('common.page')} {safePage} / {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Select value={pageSize} onValueChange={setPageSize}>
                    <SelectTrigger className="w-[120px] h-8" data-testid="my-contributions-page-size-select">
                      <SelectValue placeholder={t('common.pageSize')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === 'all' ? t('common.all') : option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={safePage === 1 || pageLoading || pageSize === 'all'}
                    data-testid="my-contributions-prev-btn"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={safePage >= totalPages || pageLoading || pageSize === 'all'}
                    data-testid="my-contributions-next-btn"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </div>

              {pageLoading && (
                <div className="mt-3 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContributionsPage;
